/**
 * CoverageAuditStageDashboard
 *
 * Cross-encounter view of where coverage-audit issues cluster, broken
 * down by the three pipeline stages:
 *   - recording          (doctor's discovery quality)
 *   - summary_pipeline   (transcript → summary)
 *   - note_pipeline      (summary → note)
 *
 * Drives the "Stage" tile in the Management Console — useful for the
 * platform reviewer to spot where the system is leaking before zooming
 * into a specific encounter. log_only and dismissed issues are excluded
 * server-side.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';

type AuditStage = 'recording' | 'summary_pipeline' | 'note_pipeline' | 'unknown';

interface StageBreakdown {
  stage: AuditStage;
  total: number;
  encountersAffected: number;
  topFamilies: Array<{ family: string; count: number }>;
  topDetectors: Array<{ detectorId: string; count: number }>;
  bySeverity: Record<'major' | 'moderate' | 'minor', number>;
}

interface StageAnalyticsResponse {
  scope: {
    organizationId: string | null;
    sinceDays: number;
    since: string;
  };
  stages: StageBreakdown[];
  totals: { issues: number; encounters: number };
}

interface BirdsEyeResponse {
  scope: { encountersAudited: number };
  byStage: Record<AuditStage, number>;
  totalIssues: number;
  topDetectors: Array<{ detectorId: string; stage: AuditStage; count: number }>;
  topFamilies: Array<{ family: string; count: number }>;
  issueDistribution: {
    zero: number;
    oneToThree: number;
    fourToTen: number;
    elevenToTwenty: number;
    overTwenty: number;
    median: number;
    max: number;
  };
  outliers: Array<{ encounterId: string; issueCount: number }>;
  kpi: {
    pipelineCount: number;
    recordingCount: number;
    pipelineShare: number;
  };
}

const WINDOW_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
];

const STAGE_META: Record<
  AuditStage,
  { title: string; subtitle: string; tone: string }
> = {
  recording: {
    title: 'Recording',
    subtitle: 'What the doctor captured during the visit',
    tone: 'border-emerald-200 bg-emerald-50/40',
  },
  summary_pipeline: {
    title: 'Summary pipeline',
    subtitle: 'Our AI transforming transcript → clinical summary',
    tone: 'border-purple-200 bg-purple-50/40',
  },
  note_pipeline: {
    title: 'Note pipeline',
    subtitle: 'Our AI generating the medical note from the summary',
    tone: 'border-blue-200 bg-blue-50/40',
  },
  unknown: {
    title: 'Unknown',
    subtitle: 'Detector ids not classified by the stage map',
    tone: 'border-gray-200 bg-gray-50/40',
  },
};

export default function CoverageAuditStageDashboard() {
  const [sinceDays, setSinceDays] = useState<number>(30);
  const [data, setData] = useState<StageAnalyticsResponse | null>(null);
  const [birdsEye, setBirdsEye] = useState<BirdsEyeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stageResult, birdsEyeResult] = await Promise.all([
        apiService.get<StageAnalyticsResponse>(
          `/v1/coverage-audit/stage-analytics?sinceDays=${sinceDays}`,
        ),
        apiService.get<BirdsEyeResponse>(`/v1/coverage-audit/birds-eye`),
      ]);
      setData(stageResult);
      setBirdsEye(birdsEyeResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [sinceDays]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Coverage Audit — Stage Dashboard
          </h1>
          <p className="text-sm text-gray-600">
            Where pipeline issues cluster, across all encounters. Excludes
            log_only and dismissed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500">
            Window
          </label>
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(parseInt(e.target.value, 10))}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Link
            to="/management/coverage-audit"
            className="text-sm text-purple-700 hover:text-purple-900 underline"
          >
            ← Encounters
          </Link>
        </div>
      </header>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {data && (
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-800">{data.totals.issues}</span>{' '}
          actionable issues across{' '}
          <span className="font-semibold text-gray-800">
            {data.totals.encounters}
          </span>{' '}
          encounters in the last {data.scope.sinceDays} days.
        </div>
      )}

      {birdsEye && <BirdsEyeBlock data={birdsEye} />}

      {loading && !data ? (
        <div className="text-sm text-gray-500 italic border rounded p-4 bg-gray-50">
          Loading stage analytics…
        </div>
      ) : data && data.stages.length === 0 ? (
        <div className="text-sm text-gray-500 italic border rounded p-4 bg-gray-50">
          No actionable issues in this window.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {(data?.stages ?? []).map((stage) => (
            <StageCard key={stage.stage} stage={stage} />
          ))}
        </div>
      )}
    </div>
  );
}

function StageCard({ stage }: { stage: StageBreakdown }) {
  const meta = STAGE_META[stage.stage];
  return (
    <article className={`rounded border p-3 ${meta.tone}`}>
      <header className="mb-2">
        <h2 className="text-base font-semibold text-gray-900">{meta.title}</h2>
        <p className="text-[11px] text-gray-600 leading-snug">
          {meta.subtitle}
        </p>
      </header>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold text-gray-900">{stage.total}</span>
        <span className="text-xs text-gray-600">
          issues · {stage.encountersAffected} encounters
        </span>
      </div>
      <div className="flex gap-2 mb-3 text-[11px]">
        <SeverityChip label="Major" count={stage.bySeverity.major} cls="bg-red-100 text-red-800" />
        <SeverityChip label="Moderate" count={stage.bySeverity.moderate} cls="bg-amber-100 text-amber-800" />
        <SeverityChip label="Minor" count={stage.bySeverity.minor} cls="bg-yellow-100 text-yellow-800" />
      </div>
      {stage.topFamilies.length > 0 && (
        <Section title="Top families">
          {stage.topFamilies.map((f) => (
            <Row key={f.family} label={f.family} count={f.count} />
          ))}
        </Section>
      )}
      {stage.topDetectors.length > 0 && (
        <Section title="Top detectors">
          {stage.topDetectors.map((d) => (
            <Row key={d.detectorId} label={d.detectorId} count={d.count} />
          ))}
        </Section>
      )}
    </article>
  );
}

function SeverityChip({
  label,
  count,
  cls,
}: {
  label: string;
  count: number;
  cls: string;
}) {
  return (
    <span className={`px-1.5 py-0.5 rounded font-semibold ${cls}`}>
      {label} {count}
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Row({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-700 font-mono truncate" title={label}>
        {label}
      </span>
      <span className="text-gray-900 font-semibold ml-2">{count}</span>
    </div>
  );
}

function BirdsEyeBlock({ data }: { data: BirdsEyeResponse }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCell
          label="Encounters audited"
          value={String(data.scope.encountersAudited)}
        />
        <KpiCell label="Total issues (latest run)" value={String(data.totalIssues)} />
        <KpiCell
          label="Pipeline share"
          value={`${(data.kpi.pipelineShare * 100).toFixed(0)}%`}
          sub={`${data.kpi.pipelineCount} pipeline / ${data.kpi.recordingCount} recording`}
        />
        <KpiCell
          label="Issues per encounter"
          value={`median ${data.issueDistribution.median} / max ${data.issueDistribution.max}`}
          sub={`${data.issueDistribution.zero} zero · ${data.issueDistribution.oneToThree} 1–3 · ${data.issueDistribution.fourToTen} 4–10 · ${data.issueDistribution.elevenToTwenty} 11–20 · ${data.issueDistribution.overTwenty} >20`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded border border-gray-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
            Top detectors by issue volume
          </div>
          <div className="space-y-1">
            {data.topDetectors.map((d) => (
              <div
                key={d.detectorId}
                className="flex items-center justify-between text-xs"
              >
                <span className="font-mono text-gray-700 truncate" title={d.detectorId}>
                  {d.detectorId}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] uppercase text-gray-500">
                    {d.stage}
                  </span>
                  <span className="font-semibold text-gray-900 w-8 text-right">
                    {d.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {data.outliers.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50/40 p-3">
            <div className="text-[10px] uppercase tracking-wide text-amber-800 font-semibold mb-2">
              Outlier encounters (&gt;10 issues)
            </div>
            <div className="space-y-1 text-xs">
              {data.outliers.map((o) => (
                <div
                  key={o.encounterId}
                  className="flex items-center justify-between"
                >
                  <Link
                    to={`/management/coverage-audit/encounter/${o.encounterId}`}
                    className="font-mono text-purple-700 hover:underline truncate"
                    title={o.encounterId}
                  >
                    {o.encounterId.slice(0, 8)}…
                  </Link>
                  <span className="font-semibold text-amber-900">
                    {o.issueCount} issues
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCell({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {label}
      </div>
      <div className="text-lg font-bold text-gray-900 mt-1">{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}
